import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import {
  INPUT_LENGTH_15,
  INPUT_LENGTH_500,
  SWAL_SETTINGS,
} from "../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { updateExtraDetails, extraDetails } from "../../services/doctor.services";
import { Tooltip, Whisper } from "rsuite";

const DoctorExtraDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const doctorId = params.id;
  const [submitted, setSubmitted] = useState(false);
  const [previewimage, setPreviewImage] = useState("");
  const [defaultData, setDefaultData] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  const breadcrumbs = [
    { title: t("admin.cafes.link_dashboard"), url: "/admin/dashboard/1" },
    {
      title: t("Dooctor Management"),
      url: "/admin/doctor-management/doctor/list/1",
    },
    { title: "More Doctor Details", url: "" },
  ];

  const validationSchema = Yup.object().shape({
      about_doctor: Yup.string().trim().max(1000, "About doctor must not be more than 1000 characters.").required("About doctor is required"),

      total_questions_answers: Yup.number().required("Total questions and answers is required").min(0, "Total questions and answers cannot be less than 0"),
      awards: Yup.number().required("Awards is required").min(0, "Awards cannot be less than 0"),

      positive_review_percentage: Yup.number()
          .required("Positive review percentage is required")
          .min(0, "Positive review percentage cannot be less than 0")
          .max(100, "Positive review percentage cannot be more than 100"),

      doctor_image: Yup.mixed()
          .required("Doctor image is required")
          .test("fileOrUrl", "Doctor image is required", (value) => {
              if (!value) return false;
              if (typeof value === "string") return value.trim() !== "";
              if (typeof value === "object") return true;
              return false;
          })
          .test("fileType", "Unsupported file type.", (value) => {
              if (!value || typeof value === "string") return true;
              return ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type);
          })
          .test("fileSize", "Image size must be less than 1.5 MB", (value) => {
              if (!value || typeof value === "string") return true;
              return value.size <= 1.5 * 1024 * 1024;
          })
          .test("validImage", "Upload a valid image", (value) => {
              if (!value || typeof value === "string") return true;

              return new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                      const img = new Image();
                      img.onload = () => resolve(true);
                      img.onerror = () => resolve(false);
                      img.src = reader.result;
                  };
                  reader.onerror = () => resolve(false);
                  reader.readAsDataURL(value);
              });
          }),
  });


  useEffect(() => {
    globalLoader(true);
    extraDetails(doctorId)
      .then((res) => {
        if (res.success && res.data) {
          const data = res.data;
          setDefaultData(data);
          if (data.doctor_image) {
            setPreviewImage(data.doctor_image);
          }
        }
        globalLoader(false);
      })
      .catch((err) => {
        console.log(err);
        globalLoader(false);
      });
  }, [doctorId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      about_doctor: defaultData.about_doctor || "",
      total_questions_answers: defaultData.total_questions_answers || "",
      positive_review_percentage: defaultData.positive_review_percentage || "",
      awards: defaultData.awards || "",
      doctor_image: defaultData.doctor_image || null,
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
        formData.append("o_id", doctorId);
        formData.append("about_doctor", values.about_doctor);
        formData.append("total_questions_answers", values.total_questions_answers);
        formData.append("positive_review_percentage", values.positive_review_percentage);
        formData.append("awards", values.awards);
      if (values.doctor_image) {
        formData.append("doctor_image", values.doctor_image);
      }

      updateExtraDetails(formData)
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            navigate(`/admin/doctor-management/doctor/list/1`);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("error ====> ", error);
          globalLoader(false);
          setSubmitted(false);
        });
    },
  });

  console.log("formik values", formik.values);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">
                  {previewimage ? "Edit Doctor Extra Details" : "Add Doctor Extra Details"}
                </h6>

                <div className="row row-sm">
                  <div className="col-lg-6 form-group">
                    <label htmlFor="positive_review_percentage" className="d-flex">
                      Positive Review Percentage: <span className="requirestar">*</span>
                    </label>
                    <input
                      name="positive_review_percentage"
                      type="number"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.positive_review_percentage}
                      placeholder="Enter positive review percentage"
                    />
                    <span className="text-danger">
                      <CustomError name="positive_review_percentage" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="total_questions_answers" className="d-flex">
                      Total Question and Answers: <span className="requirestar">*</span>
                    </label>
                    <input
                      name="total_questions_answers"
                      type="number"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.total_questions_answers}
                      placeholder="Enter number"
                    />
                    <span className="text-danger">
                      <CustomError name="total_questions_answers" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="awards">Awards:</label>
                    <input
                      name="awards"
                      type="text"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.awards}
                      placeholder="Enter awards"
                    />
                    <span className="text-danger">
                      <CustomError name="awards" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="about_doctor" className="d-flex">
                      About Doctor: <span className="requirestar">*</span>
                    </label>
                    <textarea
                      name="about_doctor"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.about_doctor}
                      placeholder="Write something about the doctor"
                    />
                    <span className="text-danger">
                      <CustomError name="about_doctor" form={formik} />
                    </span>
                  </div>

                  <div className="col-lg-6 form-group">
                    <label htmlFor="doctor_image" className="d-flex">
                      {t("admin.cafes.label_logo")}
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{t("admin.cafes.image_support_tooltip") + " (Background transparent required)"}</Tooltip>}
                      >
                        <span className="field-more-info ms-1 cp">?</span>
                      </Whisper>
                    </label>
                    <input
                      className="form-control imgInput"
                      id="doctor_image"
                      name="doctor_image"
                      accept="image/*"
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files[0];
                        formik.setFieldValue("doctor_image", file);
                        if (file) {
                          setPreviewImage(URL.createObjectURL(file));
                        } else {
                          setPreviewImage("");
                        }
                      }}
                    />
                    <span className="text-danger">
                      <CustomError name="doctor_image" form={formik} />
                    </span>

                    {previewimage && (
                      <ul className="question-image-preview questions-ul mt-2">
                        <li className="pr_img_box">
                          <img
                            src={previewimage}
                            style={{ height: "100px" }}
                            alt="Preview"
                          />
                          <div className="img_options">
                            <button
                              type="button"
                              className="text-danger"
                              onClick={() => {
                                setPreviewImage("");
                                formik.setFieldValue("doctor_image", null);
                                document.getElementById("doctor_image").value = "";
                              }}
                            >
                              <i className="ri-delete-bin-6-fill"></i>
                            </button>
                          </div>
                        </li>
                      </ul>
                    )}
                  </div>

                  <div className="mt-5">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("admin.button.btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("admin.button.btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default DoctorExtraDetails;
